import { CurrencyPairRate, RateAlertCompetitor, RateAlertRule, RateProvider } from "../../../redux/type";

export type {
  RateProvider,
  RatePosition,
  RateProviderQuote,
  CurrencyPairRate,
  RateHistoryPeriod,
  GetRateComparisonParams,
  GetRateHistoryParams,
  GetRateAlertsParams,
  RatePaginationMeta,
  AlertStatus,
  RateAlertMode,
  RateAlertCompetitor,
  RateAlertRule,
  RateAlertPayload,
  UpdateRateAlertPayload,
  CurrencyPairOption,
  ApiRateQuote,
  ApiRateComparisonItem,
} from "../../../redux/type";

export const COMPARISON_PROVIDER_ORDER: RateProvider[] = [
  "smartsend",
  "wise",
  "remitly",
  "lemfi",
  "taptap_send",
  "others",
];

// History view drops the "[others]" column per design.
export const HISTORY_PROVIDER_ORDER: RateProvider[] = [
  "smartsend",
  "wise",
  "remitly",
  "lemfi",
  "taptap_send",
];

export const PROVIDER_LABEL: Record<RateProvider, string> = {
  smartsend: "SmartSend",
  wise: "Wise",
  remitly: "Remitly",
  lemfi: "LemFi",
  taptap_send: "Taptap Send",
  others: "[others]",
};

export const PERIOD_OPTIONS = [
  { label: "Latest rates", value: "latest" as const },
  { label: "Last 24 hours", value: "24h" as const },
  { label: "Last 7 days", value: "7d" as const },
  { label: "Custom date", value: "custom" as const },
];

export const ALERT_COMPETITOR_OPTIONS: { label: string; value: RateAlertCompetitor }[] = [
  { label: "Wise", value: "wise" },
  { label: "Remitly", value: "remitly" },
  { label: "LemFi", value: "lemfi" },
  { label: "Taptap Send", value: "taptapsend" },
];

export const ALERT_COMPETITOR_LABEL: Record<RateAlertCompetitor, string> = {
  wise: "Wise",
  remitly: "Remitly",
  lemfi: "LemFi",
  taptapsend: "Taptap Send",
};

export function formatAlertCondition(rule: RateAlertRule): string {
  const pairsLabel =
    rule.currency_pairs && rule.currency_pairs.length > 0
      ? rule.currency_pairs.map((p) => p.toUpperCase()).join(", ")
      : "All pairs";
  const competitorsLabel =
    rule.competitors && rule.competitors.length > 0
      ? rule.competitors.map((c) => ALERT_COMPETITOR_LABEL[c]).join(", ")
      : "All competitors";
  const modeLabel =
    rule.mode === "behind_only"
      ? `falls behind SmartSend by ${rule.min_change_percent}%+`
      : `changes by ${rule.min_change_percent}%+`;

  return `${pairsLabel} · ${competitorsLabel} · ${modeLabel}`;
}

// True when SmartSend has a quote for this pair but no competitor does —
// there's nothing to be "leading" or "behind" against, so this is its own
// distinct state ("Exclusive") rather than being folded into "Leading".
export function isExclusiveRate(pair: CurrencyPairRate): boolean {
  if (pair.quotes.smartsend.rate === null) return false;
  return (Object.keys(pair.quotes) as RateProvider[])
    .filter((provider) => provider !== "smartsend")
    .every((provider) => pair.quotes[provider].rate === null);
}
